import api from '../../services/api'

export interface ScoreRow {
  org_nr: string
  navn: string | null
  nace_kode: string | null
  nace_beskrivelse: string | null
  total_score: number
  /** Bil-score per bil i flåten. total_score alene rangerer etter flåtestørrelse
   *  — 40 kalde biler slår 3 leasingavtaler som går ut — så dette er kolonnen
   *  lista bør sorteres på. Org-scorene (ny NACE-org, vekst, konkurs) er holdt
   *  utenfor telleren. */
  leads_faktor: number
  /** Nevneren bak leads_faktor: hele personbil+varebil-flåten. Den ignorerer
   *  vehicle_class-filteret med vilje, så den kan avvike fra total_vehicles. */
  leads_faktor_grunnlag: number
  eligible_car_count: number
  total_vehicles: number
  hottest_category: string | null
  postnummer: string | null
  kommune: string | null
  /** Ownership form and vehicle class, derived from the same SVVState scan as
   *  total_vehicles — owned + leased and personbil + varebil both sum to it. */
  owned_cars?: number
  leased_cars?: number
  personbil_count?: number
  varebil_count?: number
}

/** Paged list responses stop counting at 1000 rather than running an exact
 *  COUNT over the whole match set. When total_is_capped is true, `total` is a
 *  floor (1000) and not the real number, so page counts must not be derived
 *  from it — use has_more, which stays exact because the backend over-fetches
 *  one row. */
export interface CappedPage {
  total: number
  total_is_capped: boolean
  has_more: boolean
}

/** Biler og Leads summert over hele trefflista, ikke bare siden. Uavhengig av
 *  `total`, som stopper på 1 000. Med vehicle_class satt summeres bare den
 *  klassen (#627 AC1). */
export interface ScoreTotals {
  org_count: number
  total_vehicles: number
  eligible_car_count: number
}

/** Set when the search was a bedriftsnummer: the backend then also matched
 *  its hovedenhet (#1008). Null for every other search. */
export interface UnderenhetMatch {
  bedriftsnummer: string
  navn: string
  overordnet_enhet: string
}

export interface ScoresResponse extends CappedPage {
  totals: ScoreTotals
  items: ScoreRow[]
  underenhet_match: UnderenhetMatch | null
}

export interface LeadRow {
  id: number
  kjennemerke: string | null
  /** Stable vehicle identity, for the eierhistorikk walk-back. Null when the
   *  lead's plate has no SVVState row. */
  kuid: string | null
  org_nr: string
  org_navn: string | null
  category_code: string
  category_name: string
  lead_level: string
  lead_score: number
  merke: string | null
  modell: string | null
  vehicle_class: string | null
  registrert_forste_gang: string | null
  registrert_forste_gang_pa_eier: string | null
  start_date: string
  expires_at: string
  active: boolean
}

export interface LeadsResponse extends CappedPage {
  items: LeadRow[]
  underenhet_match: UnderenhetMatch | null
}

export interface LeadCategory {
  id: number
  code: string
  name: string
  description: string | null
  lead_level: string
  priority: number
  lead_score: number
  ttl_days: number
  active: number
}

export interface NaceCode {
  nace_kode: string
  nace_beskrivelse: string | null
  org_count: number
  total_owned: number
  total_leased: number
  scored_org_count: number
}

export interface NaceCodesResponse {
  total: number
  items: NaceCode[]
}

export interface NaceCodesParams {
  markedsomrade_ids?: string
  limit?: number
  offset?: number
}

export interface ScoresParams {
  markedsomrade_ids?: string
  name?: string
  nace_kode?: string
  category_code?: string
  vehicle_class?: string
  mine_nace_koder?: boolean
  min_score?: number
  sort_by?: string
  limit?: number
  offset?: number
}

export interface LeadsParams {
  markedsomrade_ids?: string
  org_nr?: string
  category_code?: string
  vehicle_class?: string
  min_score?: number
  lead_level?: string
  active?: boolean
  mine_nace_koder?: boolean
  sort_by?: string
  sort_dir?: 'asc' | 'desc'
  limit?: number
  offset?: number
}

export interface OrgDetail {
  organisasjonsnummer: string
  navn: string
  organisasjonsform_kode: string | null
  organisasjonsform_beskrivelse: string | null
  naeringskode1_kode: string | null
  naeringskode1_beskrivelse: string | null
  antall_ansatte: number | null
  hjemmeside: string | null
  epostadresse: string | null
  telefon: string | null
  mobil: string | null
  forretningsadresse_adresse: string | null
  forretningsadresse_poststed: string | null
  forretningsadresse_postnummer: string | null
  forretningsadresse_kommune: string | null
  forretningsadresse_kommunenummer: string | null
  stiftelsesdato: string | null
  registreringsdato_enhetsregisteret: string | null
  konkurs: number
  under_avvikling: number
  total_score: number | null
  eligible_car_count: number | null
  hottest_category: string | null
  /** Brreg underenheter under this org — the Underenheter tab shows at 2+ (#1002). */
  underenhet_count: number
}

/** One underenhet (avdeling) of a hovedenhet, from Brreg (#1002). */
export interface Underenhet {
  bedriftsnummer: string
  navn: string
  postnummer: string | null
  poststed: string | null
  /** Active cars registered on the bedriftsnummer itself — not the branch's share of the fleet. */
  car_count: number
}

export interface UnderenheterResponse {
  total: number
  has_more: boolean
  items: Underenhet[]
}

export interface FollowUpEntry {
  id: number
  user_id: string
  comment: string
  status: string | null
  created_at: string
}

export interface FollowUpSummary {
  target_org_nr: string
  status: string
  last_followed_up_at: string
  last_followed_up_by: string
  follow_up_count: number
}

export interface FollowUpResponse {
  summary: FollowUpSummary | null
  history: FollowUpEntry[]
}

export interface FollowUpCreatePayload {
  target_org_nr: string
  comment: string
  status?: string
}

export interface OrgVehicle {
  kjennemerke: string
  /** Stable vehicle identity, for the eierhistorikk walk-back. */
  kuid: string | null
  merke: string | null
  modell: string | null
  drivstofftype: string | null
  vehicle_class: string | null
  registrert_forste_gang: string | null
  registrert_forste_gang_pa_eier: string | null
  leasingbil: boolean
  /** Owner org_nr — for a leased car the owner is the leasing company. Null when owned. */
  leasing_company_org_nr: string | null
  /** Shortened display name, backend-resolved. Null when owned. */
  leasing_company_navn: string | null
  farge: string | null
  has_active_lead: boolean
  /** Matches the Biler search the user arrived with (#625). Always false
   *  without one. */
  is_match: boolean
  /** Set when the car is registered on one of the org's bedriftsnumre (#1006). */
  underenhet: { bedriftsnummer: string; navn: string } | null
}

export interface OrgVehiclesResponse extends CappedPage {
  items: OrgVehicle[]
  /** Number of matching cars — present only when a Biler search was sent. */
  match_total?: number
}

export interface OrgLeasingselskap {
  org_nr: string
  navn: string | null
  car_count: number
}

/** When the bestand sweep last checked the org against SVV (#640). Null when
 *  it never has. last_checked_at is UTC, ISO 8601 with Z. */
export interface BestandStatus {
  last_checked_at: string | null
  vehicles_held: number | null
}

/** One active lien on a car, from Brreg (#943). */
export interface OrgHeftelse {
  dokumentnummer: string | null
  type_kode: string | null
  type: string | null
  kategori: 'finansiering' | 'tvang' | 'annet' | null
  tinglyst: string | null
  /** null for leasing: no amount, not zero */
  belop: number | null
  valuta: string | null
  rettighetshaver_etikett: string | null
  rettighetshaver_navn: string | null
  rettighetshaver_org_nr: string | null
  /** Vehicles the lien covers in total; over 1 means the amount is shared */
  antall_kjoretoy: number | null
}

/** leasing_svv: no lien in Brreg, but SVV says the car is leased — fleet
 *  lessors such as Arval never register in Løsøreregisteret */
export type HeftelseBilStatus = 'heftelse' | 'leasing_svv' | 'ingen' | 'ikke_sjekket'

export interface OrgHeftelserResponse {
  org_nr: string
  /** null when the org is over maks_biler */
  antall_biler: number | null
  maks_biler: number
  over_grense: boolean
  checked_at: string | null
  neste_sjekk: string | null
  kan_sjekkes: boolean
  antall: Record<HeftelseBilStatus, number>
  per_rettighetshaver: {
    navn: string | null
    org_nr: string | null
    kategori: OrgHeftelse['kategori']
    antall_biler: number
    /** Each document's amount once, however many cars it covers */
    sum_belop: number | null
  }[]
  biler: {
    kjennemerke: string
    merke: string | null
    modell: string | null
    status: HeftelseBilStatus
    heftelser: OrgHeftelse[]
    /** The SVV owner of a leased car, i.e. the lessor */
    svv_leasing: { org_nr: string; navn: string | null } | null
  }[]
  /** The user's daily Brreg budget, one unit per car — shared with Oppslag */
  kvote: { budsjett: number; brukt: number; gjenstaende: number }
}

export type OvrigTypeGroup =
  | 'tilhenger' | 'lastebil' | 'traktor' | 'traktortilhenger'
  | 'buss' | 'mc_moped' | 'motorredskap' | 'annet'

/** A vehicle the org holds outside personbil/varebil (#640). */
export interface OvrigKjoretoy {
  kjennemerke: string
  kuid: string | null
  merke: string | null
  modell: string | null
  /** SVV Teknisk_kjoretoygruppe_Verdi, e.g. "Tilhenger - totalvekt inntil 750 kg" */
  type: string | null
  type_kode: string | null
  type_group: OvrigTypeGroup
  drivstofftype: string | null
  registrert_forste_gang: string | null
  registrert_forste_gang_pa_eier: string | null
  /** REGISTRERT or AVREGISTRERT */
  registreringsstatus: string | null
  leasingbil: boolean
  leasing_company_org_nr: string | null
  leasing_company_navn: string | null
}

export interface OvrigeKjoretoyResponse extends CappedPage {
  items: OvrigKjoretoy[]
}

export interface NewsCategoryCount {
  category_id: number
  category_code: string
  category_name: string
  count: number
}

// --- Nyheter drill-down + paginated grids (#392) ----------------------------

export interface NewsCategoryOrg {
  org_nr: string
  navn: string | null
  poststed: string | null
  lead_count: number
  count_of_owned_cars: number
  count_of_leased_cars: number
}

export interface NewsNewOrg {
  org_nr: string
  navn: string | null
  nace_kode: string | null
  nace_beskrivelse: string | null
  antall_ansatte: number | null
  postnummer: string | null
  poststed: string | null
  registrert: string | null
  /** Stiftelsesdato; null for about a third of new orgs (#627 AC7) */
  stiftet: string | null
}

export interface NewsGrowthOrg {
  org_nr: string
  navn: string | null
  poststed: string | null
  old_value: number | null
  new_value: number | null
  pct_change: number | null
  /** When the Brreg import saw the growth, not when headcount changed (#627 AC8) */
  oppdaget: string | null
}

export interface NewsPage<T> extends CappedPage {
  items: T[]
}

export interface NewsOrgItem {
  org_nr: string
  navn: string | null
  postnummer: string | null
  registrert: string | null
}

export interface NewsGrowthItem {
  org_nr: string
  navn: string | null
  old_value: number | null
  new_value: number | null
  pct_change: number | null
}

/** Items are capped at 20 and are [] whenever the matching count is 0. */
export interface NewsSection<T> {
  count: number
  items: T[]
}

export interface NewsResponse {
  new_leads: NewsCategoryCount[]
  expired_leads: NewsCategoryCount[]
  vehicles_in: number
  new_orgs: NewsSection<NewsOrgItem>
  employee_growth: NewsSection<NewsGrowthItem>
}

// #438 AC2: filter bar on Nyheter. NACE/mine-NACE only reach the two org grids
// and the category drill-down — /leads/news itself is a precomputed aggregate
// with no NACE dimension, so its counts stay markedsomrade-scoped only.
export interface NewsMarkedsomradeFilter {
  markedsomrade_ids?: string
}

export interface NewsDrillDownFilter extends NewsMarkedsomradeFilter {
  nace_kode?: string
  mine_nace_koder?: boolean
}

// #438 AC3: sortable columns on the Nye organisasjoner / Vekst i ansatte grids.
export interface NewsOrgsFilter extends NewsDrillDownFilter {
  sort_by?: string
  sort_dir?: 'asc' | 'desc'
}

// --- Hendelser tab (#369) ---------------------------------------------------

export type HendelseScope = 'mine_org' | 'mine_leads' | 'begge'
export type HendelseType = 'eie' | 'leasing' | 'solgt' | 'kjopt'

export interface HendelserParams {
  from: string
  to: string
  scope?: HendelseScope
  org?: string
  type?: HendelseType
  merke?: string[]
  /** Car's first registration, YYYY-MM — a different axis from from/to, which
   *  bound when the event happened. */
  reg_from?: string
  reg_to?: string
  page?: number
  page_size?: number
}

export interface HendelseTabRow {
  kjennemerke: string | null
  understellsnummer: string | null
  merke: string | null
  modell: string | null
  registrert: string
  forstegang_reg: string | null
  org_nr: string
  org_navn: string | null
  postnummer: string | null
  poststed: string | null
  type: HendelseType
  source: 'nyreg' | 'bruktbil'
  hendelse_tidspunkt: string | null
}

export interface HendelsePerOrg {
  org_nr: string
  org_navn: string | null
  total: number
  eie: number
  leasing: number
  solgt: number
  kjopt: number
}

export interface HendelserTabResponse {
  summary: HendelseSummary
  per_org: HendelsePerOrg[]
  rows: HendelseTabRow[]
  pagination: { page: number; page_size: number; total: number; total_pages: number }
  /** The scope org list was truncated — the result covers only part of it. */
  scope_is_capped: boolean
  /** Matching rows were truncated before aggregation, so the summary describes
   *  the truncated set rather than everything that matched. */
  result_is_capped: boolean
}

export interface HendelseMerkeFacet {
  merke: string
  car_count: number
}

export interface Bookmark {
  id: number
  user_id: string
  org_nr: string
  shared: boolean
  note: string | null
  created_at: string
  navn: string | null
  total_score: number | null
  hottest_category: string | null
  follow_up_status: string | null
  is_own: boolean
}

export type RegnskapFlag = 'green' | 'yellow' | 'warning'

export interface RegnskapMetric {
  value: number | null
  unit: string
  flag: RegnskapFlag
}

export interface RegnskapYear {
  year: number | null
  period: { fra: string | null; til: string | null }
  revidert: boolean
  regnskapsregler: string | null
  raw: {
    sum_driftsinntekter: number | null
    driftsresultat: number | null
    aarsresultat: number | null
    sum_eiendeler: number | null
    sum_egenkapital: number | null
    sum_gjeld: number | null
    sum_kortsiktig_gjeld: number | null
    sum_langsiktig_gjeld: number | null
    sum_omloepsmidler: number | null
    sum_anleggsmidler: number | null
  }
  metrics: {
    egenkapitalandel: RegnskapMetric
    likviditetsgrad: RegnskapMetric
    resultat: RegnskapMetric
    driftsmargin: RegnskapMetric
    gjeldsgrad: RegnskapMetric
    omsetning: RegnskapMetric
  }
  overall: RegnskapFlag
}

export interface RegnskapResponse {
  org_nr: string
  years: RegnskapYear[]
  trend: {
    omsetning: 'up' | 'down' | 'stable' | null
    resultat: 'up' | 'down' | 'stable' | null
  }
}

export interface HendelseSummary {
  total_cars: number
  total_orgs: number
  eie_count: number
  leasing_count: number
  solgt_count: number
  kjopt_count: number
  // only present on /leads/organizations/{org_nr}/hendelser (#326)
  vraket_count?: number
  utfort_count?: number
}

export interface HendelseRow {
  merke: string | null
  modell: string | null
  // present on /leads/organizations/{org_nr}/hendelser only (#331)
  kjennemerke?: string | null
  registrert: string
  postnummer: string | null
  poststed: string | null
  type: 'eie' | 'leasing' | 'solgt' | 'kjopt' | 'vraket' | 'utfort'
  org_nr: string
  org_navn: string | null
  source: 'nyreg' | 'bruktbil' | 'endstate'
  hendelse_tidspunkt: string | null
}

export interface HendelserResponse {
  summary: HendelseSummary
  rows: HendelseRow[]
  pagination: {
    page: number
    page_size: number
    total: number
    total_pages: number
  }
}

export interface BilerMerkeFacet {
  merke_id: number
  merke_navn: string | null
  car_count: number
}

export interface BilerModellFacet {
  modell: string
  car_count: number
}

export interface BilerOrgRow {
  org_nr: string
  navn: string | null
  car_count: number
  leased_count: number
  oldest_reg: string | null
  newest_reg: string | null
  postnummer: string | null
}

export interface BilerOrganisasjonerResponse extends CappedPage {
  items: BilerOrgRow[]
}

export interface BilerSearchFilters {
  merke_id?: number
  /** Exact Modell strings from the facet. Sent as repeated params — model
   *  strings can contain commas ("HIACE 2,5"), so CSV would corrupt them. */
  modell?: string[]
  reg_from?: string // YYYY-MM inclusive
  reg_to?: string // YYYY-MM inclusive
  vehicle_class?: string
  eierskap?: 'eid' | 'leaset'
  markedsomrade_ids?: string
}

/** Build the query string by hand: the shared axios paramsSerializer mangles
 *  array params (see the bruktbil merke[] incident), and modell must repeat. */
function bilerQuery(filters: BilerSearchFilters, extra?: Record<string, string | number>): string {
  const qs = new URLSearchParams()
  if (filters.merke_id !== undefined) qs.set('merke_id', String(filters.merke_id))
  for (const m of filters.modell ?? []) qs.append('modell', m)
  if (filters.reg_from) qs.set('reg_from', filters.reg_from)
  if (filters.reg_to) qs.set('reg_to', filters.reg_to)
  if (filters.vehicle_class) qs.set('vehicle_class', filters.vehicle_class)
  if (filters.eierskap) qs.set('eierskap', filters.eierskap)
  if (filters.markedsomrade_ids) qs.set('markedsomrade_ids', filters.markedsomrade_ids)
  for (const [k, v] of Object.entries(extra ?? {})) qs.set(k, String(v))
  return qs.toString()
}

export interface BilerModellBreakdown {
  merke: string | null
  modell: string | null
  car_count: number
}

/** The Biler search in its URL form (#625): the b* keys carried by the
 *  Leads URL (so Tilbake and refresh keep the search) and by the "Se" link
 *  into a bedrift (so its Kjøretøy tab can sort the matches first). modell
 *  repeats — the strings can contain commas. */
export function bilerSearchToParams(f: BilerSearchFilters, qs = new URLSearchParams()): URLSearchParams {
  if (f.merke_id !== undefined) qs.set('bmerke', String(f.merke_id))
  for (const m of f.modell ?? []) qs.append('bmodell', m)
  if (f.reg_from) qs.set('bfrom', f.reg_from)
  if (f.reg_to) qs.set('bto', f.reg_to)
  if (f.eierskap) qs.set('beier', f.eierskap)
  if (f.vehicle_class) qs.set('bvc', f.vehicle_class)
  if (f.markedsomrade_ids) qs.set('bmo', f.markedsomrade_ids)
  return qs
}

/** Inverse of bilerSearchToParams; null when the URL carries no search. Key
 *  presence decides, not value: an empty `bmerke=` is a search with no
 *  filter at all, which the Biler tab allows. */
export function bilerSearchFromParams(p: URLSearchParams): BilerSearchFilters | null {
  if (!['bmerke', 'bmodell', 'bfrom', 'bto', 'beier'].some(k => p.has(k))) return null
  const merke = p.get('bmerke')
  const modell = p.getAll('bmodell')
  const regFrom = p.get('bfrom')
  const regTo = p.get('bto')
  const eierskap = p.get('beier')
  return {
    merke_id: merke ? Number(merke) : undefined,
    modell: modell.length ? modell : undefined,
    reg_from: regFrom ?? undefined,
    reg_to: regTo ?? undefined,
    eierskap: eierskap === 'eid' || eierskap === 'leaset' ? eierskap : undefined,
    vehicle_class: p.get('bvc') ?? undefined,
    markedsomrade_ids: p.get('bmo') ?? undefined,
  }
}

export const leadsAPI = {
  getScores: (params: ScoresParams) =>
    api.get<ScoresResponse>('/leads/scores', { params }),

  getLeads: (params: LeadsParams) =>
    api.get<LeadsResponse>('/leads/leads', { params }),

  getCategories: () =>
    api.get<LeadCategory[]>('/leads/categories'),

  getNaceCodes: (params?: NaceCodesParams) =>
    api.get<NaceCodesResponse>('/leads/nace-codes', { params }),

  getOrgDetail: (orgNr: string) =>
    api.get<OrgDetail>(`/leads/organizations/${orgNr}`),

  getFollowUps: (orgNr: string) =>
    api.get<FollowUpResponse>(`/leads/follow-ups/${orgNr}`),

  createFollowUp: (payload: FollowUpCreatePayload) =>
    api.post('/leads/follow-ups', payload),

  getOrgVehicleMerker: (orgNr: string) =>
    api.get<{ items: { merke: string; car_count: number }[] }>(
      `/leads/organizations/${orgNr}/vehicles/merker`,
    ),

  getOrgVehicleLeasingselskap: (orgNr: string) =>
    api.get<{ items: OrgLeasingselskap[] }>(
      `/leads/organizations/${orgNr}/vehicles/leasingselskap`,
    ),

  getOrgVehicles: (orgNr: string, params?: {
    vehicle_class?: string; merke?: string; leasing?: boolean
    leasing_company?: string
    sort_by?: string; sort_dir?: 'asc' | 'desc'
    limit?: number; offset?: number
  }, match?: BilerSearchFilters | null) => {
    // the b* search goes by hand: bmodell repeats (see bilerQuery)
    const qs = match ? `?${bilerSearchToParams(match).toString()}` : ''
    return api.get<OrgVehiclesResponse>(`/leads/organizations/${orgNr}/vehicles${qs}`, { params })
  },

  getUnderenheter: (orgNr: string, params?: { limit?: number; offset?: number }) =>
    api.get<UnderenheterResponse>(`/leads/organizations/${orgNr}/underenheter`, { params }),

  getBestandStatus: (orgNr: string) =>
    api.get<BestandStatus>(`/leads/organizations/${orgNr}/bestand-status`),

  getOrgHeftelser: (orgNr: string) =>
    api.get<OrgHeftelserResponse>(`/leads/organizations/${orgNr}/heftelser`),

  /** Live Brreg lookup of every active car — up to 500 plates, ~20 s. */
  checkOrgHeftelser: (orgNr: string) =>
    api.post<OrgHeftelserResponse>(`/leads/organizations/${orgNr}/heftelser`),

  getOvrigeKjoretoy: (orgNr: string, params?: {
    type?: OvrigTypeGroup; merke?: string; leasing?: boolean
    leasing_company?: string
    sort_by?: string; sort_dir?: 'asc' | 'desc'
    limit?: number; offset?: number
  }) =>
    api.get<OvrigeKjoretoyResponse>(`/leads/organizations/${orgNr}/ovrige-kjoretoy`, { params }),

  getOvrigeMerker: (orgNr: string) =>
    api.get<{ items: { merke: string; car_count: number }[] }>(
      `/leads/organizations/${orgNr}/ovrige-kjoretoy/merker`,
    ),

  getOvrigeTyper: (orgNr: string) =>
    api.get<{ items: { type: OvrigTypeGroup; car_count: number }[] }>(
      `/leads/organizations/${orgNr}/ovrige-kjoretoy/typer`,
    ),

  getOvrigeLeasingselskap: (orgNr: string) =>
    api.get<{ items: OrgLeasingselskap[] }>(
      `/leads/organizations/${orgNr}/ovrige-kjoretoy/leasingselskap`,
    ),

  getRegnskap: (orgNr: string) =>
    api.get<RegnskapResponse>(`/leads/organizations/${orgNr}/regnskap`),

  getMineNaceKoder: () =>
    api.get<{ id: number; nace_kode: string; created_at: string }[]>('/leads/mine-nace-koder'),

  addMineNaceKode: (nace_kode: string) =>
    api.post('/leads/mine-nace-koder', { nace_kode }),

  addMineNaceKoderBatch: (nace_koder: string) =>
    api.post<{ added: number }>('/leads/mine-nace-koder/batch', { nace_koder }),

  deleteMineNaceKode: (nace_kode: string) =>
    api.delete(`/leads/mine-nace-koder/${nace_kode}`),

  searchNaceCodes: (search?: string, limit?: number) =>
    api.get<{ nace_kode: string; nace_beskrivelse: string | null }[]>(
      '/leads/nace-codes/search', { params: { search, limit } },
    ),

  getBookmarks: () =>
    api.get<Bookmark[]>('/leads/bookmarks'),

  addBookmark: (org_nr: string) =>
    api.post('/leads/bookmarks', { org_nr }),

  removeBookmark: (org_nr: string) =>
    api.delete(`/leads/bookmarks/${org_nr}`),

  toggleBookmarkShared: (org_nr: string, shared: boolean) =>
    api.patch(`/leads/bookmarks/${org_nr}`, { shared }),

  /** null or "" clears the note. Max 500 characters. */
  setBookmarkNote: (org_nr: string, note: string | null) =>
    api.patch(`/leads/bookmarks/${org_nr}`, { note }),

  getNews: (period: string = 'week', filter?: NewsMarkedsomradeFilter) =>
    api.get<NewsResponse>('/leads/news', { params: { period, ...filter } }),

  getNewsCategoryOrgs: (
    categoryId: number, period: string, limit: number, offset: number,
    filter?: NewsDrillDownFilter,
  ) =>
    api.get<NewsPage<NewsCategoryOrg>>('/leads/news/category-orgs', {
      params: { category_id: categoryId, period, limit, offset, ...filter },
    }),

  getNewsOrgs: <T = NewsNewOrg | NewsGrowthOrg>(
    type: 'new_org' | 'employee_growth', period: string, limit: number, offset: number,
    filter?: NewsOrgsFilter,
  ) =>
    api.get<NewsPage<T>>('/leads/news/orgs', {
      params: { type, period, limit, offset, ...filter },
    }),

  // Hendelser tab (#369). Repeated merke params are built by hand: axios's
  // serializer is not trusted with array params in this codebase — the bruktbil
  // merke[] filter was silently dropped by it once already.
  getHendelser: (params: HendelserParams) => {
    const qs = new URLSearchParams()
    qs.set('from', params.from)
    qs.set('to', params.to)
    if (params.scope) qs.set('scope', params.scope)
    if (params.org) qs.set('org', params.org)
    if (params.type) qs.set('type', params.type)
    for (const m of params.merke ?? []) qs.append('merke', m)
    if (params.reg_from) qs.set('reg_from', params.reg_from)
    if (params.reg_to) qs.set('reg_to', params.reg_to)
    if (params.page) qs.set('page', String(params.page))
    if (params.page_size) qs.set('page_size', String(params.page_size))
    return api.get<HendelserTabResponse>(`/leads/hendelser?${qs.toString()}`)
  },

  getHendelserMerker: (from: string, to: string, scope?: HendelseScope, type?: string) => {
    const qs = new URLSearchParams({ from, to })
    if (scope) qs.set('scope', scope)
    if (type) qs.set('type', type)
    return api.get<{ items: HendelseMerkeFacet[] }>(`/leads/hendelser/merker?${qs.toString()}`)
  },

  // Org-scoped events (#326): answers for ANY org in the caller's postnummer
  // scope, unlike /hendelsessenter which only covers watched orgs. Replaced
  // getHendelser (/hendelsessenter?org=...), which intersected with the
  // user's watched orgs and was empty for every org from the Bedrifter list.
  // page_size over 500 is the export (#885) and needs leads_table_exporter.
  getOrgHendelser: (orgNr: string, from: string, to: string, pageSize = 500) =>
    api.get<HendelserResponse>(`/leads/organizations/${orgNr}/hendelser`, { params: { from, to, page_size: pageSize } }),

  getBilerMerker: (markedsomradeIds?: string, vehicleClass?: string) =>
    api.get<{ items: BilerMerkeFacet[] }>(
      `/leads/biler/merker?${bilerQuery({ markedsomrade_ids: markedsomradeIds, vehicle_class: vehicleClass })}`,
    ),

  /** The period and eierskap narrow the list like the merke does (#625). */
  getBilerModeller: (
    merkeId: number, markedsomradeIds?: string, vehicleClass?: string,
    narrow?: Pick<BilerSearchFilters, 'reg_from' | 'reg_to' | 'eierskap'>,
  ) =>
    api.get<{ items: BilerModellFacet[] }>(
      `/leads/biler/modeller?${bilerQuery({
        merke_id: merkeId, markedsomrade_ids: markedsomradeIds, vehicle_class: vehicleClass, ...narrow,
      })}`,
    ),

  getBilerOrganisasjoner: (filters: BilerSearchFilters, limit: number, offset: number) =>
    api.get<BilerOrganisasjonerResponse>(
      `/leads/biler?${bilerQuery(filters, { limit, offset })}`,
    ),

  getBilerModellfordeling: (orgNr: string, filters: BilerSearchFilters) =>
    api.get<{ items: BilerModellBreakdown[] }>(
      `/leads/biler/modellfordeling?${bilerQuery(filters, { org_nr: orgNr })}`,
    ),
}
